/**
 * SmartUploadPanel — drag-and-drop multi-file uploader that uses AI to sort
 * files into the right OneDrive subfolders of a project.
 *
 * Props:
 *   project       — project object { id, name, onedrive_folder_id, … }
 *   initialFiles  — optional File[] to pre-populate (e.g. dropped on the page)
 *   onDone        — optional callback(results) called after all files are processed
 *   onClose       — optional callback — renders a close ✕ button in the header
 */
import { useRef, useState, useCallback } from "react";
import {
  ArrowsClockwise, CloudArrowUp, CheckCircle, Warning,
  ArrowsLeftRight, Plus, FileText, FilePdf, X, Sparkle,
  UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { smartUploadFile } from "../../api/microsoft";

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function ResultIcon({ result }) {
  if (result.error)
    return <Warning size={14} className="text-rose-500 flex-shrink-0" weight="fill" />;
  const s = result.saved_to || "";
  if (s.includes("replaced"))
    return <ArrowsClockwise size={14} className="text-amber-500 flex-shrink-0" weight="bold" />;
  if (s.includes("new"))
    return <Plus size={14} className="text-emerald-500 flex-shrink-0" weight="bold" />;
  if (s.includes("root"))
    return <FileText size={14} className="text-slate-400 flex-shrink-0" />;
  return <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />;
}

function ResultChip({ result }) {
  if (result.error)
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-rose-50 border border-rose-200 text-rose-700">
        error
      </span>
    );
  const s = result.saved_to || "";
  if (s.includes("replaced"))
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-50 border border-amber-200 text-amber-700">
        drawing updated
      </span>
    );
  if (s.includes("new"))
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-50 border border-emerald-200 text-emerald-700">
        new drawing
      </span>
    );
  if (s.includes("root"))
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 border border-slate-200 text-slate-600">
        saved to project
      </span>
    );
  return null;
}

// ── main component ─────────────────────────────────────────────────────────────

export default function SmartUploadPanel({ project, initialFiles, onDone, onClose }) {
  // Pre-seed file list from initialFiles (e.g. dropped on the page).
  // useState lazy initialiser runs only once per mount.
  const [files, setFiles] = useState(() => {
    if (initialFiles?.length > 0) {
      const map = new Map(Array.from(initialFiles).map((f) => [f.name, f]));
      return Array.from(map.values());
    }
    return [];
  });
  const [isDragging, setIsDragging]     = useState(false);
  const [processing, setProcessing]     = useState(false);
  const [currentIdx, setCurrentIdx]     = useState(null); // index being processed
  const [results, setResults]           = useState([]);   // completed routing results
  const fileInputRef                    = useRef(null);

  const allDone = !processing && results.length > 0 && results.length === files.length;

  // ── file selection ────────────────────────────────────────────────────────

  const addFiles = useCallback((incoming) => {
    const arr = Array.from(incoming);
    if (!arr.length) return;
    setFiles((prev) => {
      // Dedupe by name (last-wins)
      const map = new Map(prev.map((f) => [f.name, f]));
      arr.forEach((f) => map.set(f.name, f));
      return Array.from(map.values());
    });
    setResults([]); // reset results when new files are added
  }, []);

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
    setResults((prev) => prev.filter((r) => r.filename !== name));
  };

  // ── drag & drop ───────────────────────────────────────────────────────────

  const onDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = ()  => setIsDragging(false);
  const onDrop      = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // ── processing ────────────────────────────────────────────────────────────

  const handleProcess = async () => {
    if (!files.length || processing) return;
    setProcessing(true);
    setResults([]);

    const allResults = [];
    for (let i = 0; i < files.length; i++) {
      setCurrentIdx(i);
      const f = files[i];
      try {
        const res = await smartUploadFile(project.id, f);
        allResults.push({ ...res, filename: res.filename || f.name });
      } catch (e) {
        const errMsg = e.response?.data?.detail || e.message || "Upload failed";
        allResults.push({
          filename:       f.name,
          classification: "unknown",
          saved_to:       null,
          saved_to_url:   null,
          matched:        null,
          matched_url:    null,
          error:          errMsg,
        });
      }
      setResults([...allResults]);
    }

    setCurrentIdx(null);
    setProcessing(false);

    const errors = allResults.filter((r) => r.error).length;
    if (errors === 0) {
      toast.success(`All ${allResults.length} file${allResults.length === 1 ? "" : "s"} processed successfully.`);
    } else {
      toast.error(`${errors} file${errors === 1 ? "" : "s"} failed. Check the results below.`);
    }

    onDone?.(allResults);
  };

  const handleReset = () => {
    setFiles([]);
    setResults([]);
    setCurrentIdx(null);
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="smart-upload-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Sparkle size={14} className="text-violet-500" weight="fill" />
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Smart Upload
          </h3>
          {project && (
            <span className="text-[10px] text-slate-400 truncate max-w-[180px]">
              → {project.name}
            </span>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors"
            data-testid="smart-upload-close-btn"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Drop zone — hidden while processing or showing results */}
        {!processing && !allDone && (
          <>
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 py-8 border-2 border-dashed rounded-sm cursor-pointer transition-colors select-none ${
                isDragging
                  ? "border-violet-400 bg-violet-50"
                  : "border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-violet-50/30"
              }`}
              data-testid="smart-upload-drop-zone"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isDragging ? "bg-violet-100" : "bg-slate-100"
              }`}>
                <CloudArrowUp size={20} className={isDragging ? "text-violet-500" : "text-slate-400"} />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">
                  {isDragging ? "Drop files here" : "Drag & drop files, or click to browse"}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  PDFs, images, CAD files, or anything else. AI will sort them.
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="smart-upload-file-input"
              />
            </div>

            {/* Staged file list */}
            {files.length > 0 && (
              <div className="space-y-1" data-testid="smart-upload-file-list">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  {files.length} file{files.length === 1 ? "" : "s"} selected
                </p>
                {files.map((f) => (
                  <div key={f.name} className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 border border-slate-100 rounded-sm group" data-testid={`staged-file-${f.name}`}>
                    <FilePdf size={13} className="text-slate-400 flex-shrink-0" />
                    <span className="flex-1 text-xs text-slate-700 truncate">{f.name}</span>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtSize(f.size)}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"
                      data-testid={`remove-staged-file-${f.name}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Process button */}
            {files.length > 0 && (
              <button
                onClick={handleProcess}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-sm transition-colors"
                data-testid="smart-upload-process-btn"
              >
                <Sparkle size={14} weight="fill" />
                Sort {files.length} file{files.length === 1 ? "" : "s"} with AI
              </button>
            )}
          </>
        )}

        {/* Processing state */}
        {processing && (
          <div className="space-y-3" data-testid="smart-upload-processing">
            <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-100 rounded-sm">
              <ArrowsClockwise size={14} className="text-violet-500 animate-spin flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-violet-700">
                  Processing file {(currentIdx ?? 0) + 1} of {files.length}…
                </p>
                <p className="text-[10px] text-violet-500 truncate mt-0.5">
                  {files[currentIdx]?.name}
                </p>
              </div>
            </div>

            {/* Completed so far */}
            {results.length > 0 && (
              <div className="space-y-1">
                {results.map((r, i) => (
                  <ResultRow key={i} result={r} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Final results */}
        {allDone && (
          <div className="space-y-3" data-testid="smart-upload-results">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Results — {results.filter((r) => !r.error).length}/{results.length} succeeded
              </p>
              <button
                onClick={handleReset}
                className="flex items-center gap-1 text-[10px] text-violet-600 hover:text-violet-800 font-semibold uppercase tracking-wider transition-colors"
                data-testid="smart-upload-upload-more-btn"
              >
                <UploadSimple size={11} />
                Upload more
              </button>
            </div>
            <div className="space-y-1">
              {results.map((r, i) => (
                <ResultRow key={i} result={r} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Result row sub-component ──────────────────────────────────────────────────

function ResultRow({ result }) {
  return (
    <div
      className="flex items-start gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-sm text-xs"
      data-testid={`result-row-${result.filename}`}
    >
      <ResultIcon result={result} />
      <div className="flex-1 min-w-0">
        {result.saved_to_url ? (
          <a
            href={result.saved_to_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-slate-800 hover:text-[#0078D4] hover:underline truncate block"
            title={`Open ${result.filename} in OneDrive`}
          >
            {result.filename}
          </a>
        ) : (
          <span className="font-medium text-slate-800 truncate block">{result.filename}</span>
        )}
        {result.matched && (
          <p className="text-[10px] text-slate-400 mt-0.5 truncate">
            Previous version archived:{" "}
            {result.matched_url ? (
              <a href={result.matched_url} target="_blank" rel="noopener noreferrer"
                className="text-amber-600 hover:underline">{result.matched}</a>
            ) : result.matched}
          </p>
        )}
        {result.error && (
          <p className="text-[10px] text-rose-600 mt-0.5 truncate" title={result.error}>
            {result.error}
          </p>
        )}
      </div>
      <ResultChip result={result} />
    </div>
  );
}
